import ShowChartIcon from "@mui/icons-material/ShowChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
} from "@mui/material";
import { useEffect, useRef } from "react";

import { CardKind } from "../DashboardHeader/DashboardParams";

const KIND_LABEL: Record<CardKind, string> = {
  chart: "Chart",
  trend: "Trend",
};

export interface AddCardPopoverProps {
  /** Viewport coordinates to anchor at — the click that opened the flow. */
  position: { left: number; top: number };
  /** Which kinds have room at the target cell. A kind that doesn't fit is
   *  shown disabled rather than hidden, so the canvas's limits are visible. */
  fits: Record<CardKind, boolean>;
  /** Open the widget editor for a new card of this kind. */
  onPickKind: (kind: CardKind) => void;
  /** Closed without picking anything (Escape / click-away). */
  onClose: () => void;
}

/**
 * The "+" menu over an empty cell: which kind of card goes here. Its only
 * output is a kind; nothing reaches the dashboard from here.
 *
 * Anchored to pointer coordinates rather than a trigger element: the "+"
 * buttons are one per empty slot, so a single shared instance positioned at
 * the click beats a popover per slot. (That also rules out the
 * `shared/foundation` Popover, which anchors to a trigger element.)
 */
export function AddCardPopover({
  position,
  fits,
  onPickKind,
  onClose,
}: AddCardPopoverProps) {
  const listRef = useRef<HTMLUListElement>(null);

  // The popover paper takes focus on open, leaving arrow keys inert, so focus
  // the first enabled item. Not `autoFocusItem`: that lands on the first item
  // whether or not it fits at this cell.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      )
      ?.focus({ preventScroll: true });
  }, []);

  return (
    <Popover
      open
      anchorReference="anchorPosition"
      anchorPosition={position}
      onClose={onClose}
      // The paper takes focus on open but has no intrinsic name, so without
      // this a screen reader announces nothing about what opened.
      slotProps={{ paper: { "aria-label": "Add a card" } }}
    >
      <MenuList ref={listRef}>
        <MenuItem
          data-kind="chart"
          disabled={!fits.chart}
          onClick={() => onPickKind("chart")}
        >
          <ListItemIcon>
            <ShowChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={KIND_LABEL.chart} />
        </MenuItem>
        <MenuItem
          data-kind="trend"
          disabled={!fits.trend}
          onClick={() => onPickKind("trend")}
        >
          <ListItemIcon>
            <TrendingUpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={KIND_LABEL.trend} />
        </MenuItem>
      </MenuList>
    </Popover>
  );
}
